import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import halaqaService from '../../services/halaqaService';
import { getTeacherAssignments, getPendingAssessments } from '../../services/assignmentService';
import { formatDate, isToday, isPast } from '../../utils/dateUtils';
import api from '../../api/api';
import '../../styles/dashboards/TeacherDashboard.css';

// Initial empty data structure
const initialDashboardData = {
  studentCount: 0,
  pendingReviews: 0,
  attendanceRate: 0,
  pendingReviewItems: [],
  classProgress: 0
};

const TeacherDashboard = () => {
  const [data, setData] = useState(initialDashboardData);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [centerData, setCenterData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  
  // Handle logout
  const handleLogout = async () => {
    try {
      const result = await logout();
      if (result.success) {
        navigate('/login');
      } else {
        alert('Failed to logout. Please try again.');
      }
    } catch (error) {
      console.error('Error during logout:', error);
      alert('An error occurred during logout.');
    }
  };
  
  // Navigate to settings
  const handleSettingsClick = () => {
    navigate('/settings');
  };
  const [displayLimit, setDisplayLimit] = useState(5);
  const [showClassDropdown, setShowClassDropdown] = useState(false);
  
  // Week data for mini chart
  const [weekData, setWeekData] = useState([
    { label: 'M', percentage: 30 },
    { label: 'T', percentage: 45 },
    { label: 'W', percentage: 60 },
    { label: 'T', percentage: 40 },
    { label: 'F', percentage: 75 },
    { label: 'S', percentage: 20 },
    { label: 'S', percentage: 0 }
  ]);
  const [completionRate, setCompletionRate] = useState(42);
  const [weeklyAverage, setWeeklyAverage] = useState(5.2);

  const [halaqas, setHalaqas] = useState([]);
  const [currentHalaqa, setCurrentHalaqa] = useState(null);
  const [halaqaStudentCounts, setHalaqaStudentCounts] = useState({});
  const [studentCount, setStudentCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');

  // Function to toggle class dropdown
  const toggleClassDropdown = () => {
    setShowClassDropdown(!showClassDropdown);
  }

  // Function to select class
  const selectClass = (halaqa) => {
    setCurrentHalaqa(halaqa);
    setShowClassDropdown(false);
    // Fetch data for the selected class
    fetchDashboardData(halaqa);
    fetchAssignments(halaqa);
  }

  // Get priority color based on due date
  const getPriorityColor = (item) => {
    const today = new Date();
    // Use the actual dueDate field instead of updatedAt/createdAt
    const dueDate = new Date(item.dueDate || item.updatedAt || item.createdAt);

    // Overdue items (red)
    if (dueDate < today) {
      return '#f44336';
    }

    // Due today (orange)
    if (
      dueDate.getDate() === today.getDate() &&
      dueDate.getMonth() === today.getMonth() &&
      dueDate.getFullYear() === today.getFullYear()
    ) {
      return '#ff9800';
    }

    // Due in the future (blue)
    return '#4a90e2';
  }

  // Handle review item click
  const handleReviewItem = (item) => {
    const assignmentId = item.id || 'default-assignment-id';
    // Use the correct URL format that includes the assignment ID
    navigate(`/assignments/${assignmentId}/assess`, { 
      state: { 
        studentId: item.studentId || 'default-student-id',
        assignmentId: assignmentId,
        studentName: item.studentName,
        assignmentDetails: item.assignment
      }
    });
  };

  // Handle new assignment click
  const handleNewAssignment = () => {
    if (currentHalaqa) {
      navigate('/assignments/new', {
        state: {
          halaqaId: currentHalaqa._id || currentHalaqa.id,
          halaqaName: currentHalaqa.name,
          assignmentType: 'new-memorization',
          centerId: user.center
        }
      });
    } else {
      alert('No class selected. Please select a class first.');
    }
  };

  // Handle take attendance click
  const handleTakeAttendance = () => {
    navigate('/attendance');
  };

  // Handle view progress click
  const handleViewProgress = () => {
    if (currentHalaqa) {
      // Navigate directly to the class report with ID in the URL path
      navigate(`/reports/class/${currentHalaqa._id}`, {
        state: {
          halaqaId: currentHalaqa._id,
          halaqaName: currentHalaqa.name
        }
      });
    } else {
      alert('No class selected');
    }
  };

  // Handle filter change
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    console.log('Filter selected:', filter);
  };

  // Handle see all assignments click
  const handleSeeAllAssignments = () => {
    if (currentHalaqa) {
      navigate(`/classes/students`, {
        state: {
          halaqaName: currentHalaqa.name,
          centerId: user.center
        }
      });
    } else {
      alert('No class selected');
    }
  };

  // Navigate to halaqa students
  const navigateToHalaqaStudents = (halaqa) => {
    if (halaqa) {
      navigate(`/classes/${halaqa._id}/students`, {
        state: {
          halaqaName: halaqa.name,
          centerId: user.center
        }
      });
    }
  };

  // Fetch halaqa data
  const fetchHalaqaData = async () => {
    try {
      console.log('Fetching halaqa data...');
      const fetchedHalaqas = await halaqaService.getAssignedHalaqas();
      console.log('Fetched halaqas:', fetchedHalaqas);
      
      if (fetchedHalaqas && fetchedHalaqas.length > 0) {
        const halaqa = fetchedHalaqas[0];
        setCurrentHalaqa(halaqa);
        setHalaqas(fetchedHalaqas);
        
        // Fetch student counts for each halaqa
        const studentCountsObj = {};
        
        // Fetch student count for the first halaqa (current halaqa)
        try {
          const students = await halaqaService.getHalaqaStudents(halaqa._id);
          studentCountsObj[halaqa._id] = students.length;
        } catch (err) {
          console.error('Error fetching students for halaqa:', err);
          studentCountsObj[halaqa._id] = 0;
        }
        
        setHalaqaStudentCounts(studentCountsObj);
        
        console.log('Current halaqa set:', halaqa);
      } else {
        console.warn('No halaqas found for this teacher');
        setCurrentHalaqa(null);
        setHalaqas([]);
      }
    } catch (error) {
      console.error('Error fetching halaqas:', error);
      setCurrentHalaqa(null);
      setHalaqas([]);
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async (selectedHalaqa = null) => {
    try {
      // Start with empty data structure
      let dashboardData = {
        studentCount: 0,
        pendingReviews: 0,
        attendanceRate: 0,
        pendingReviewItems: [],
        classProgress: 0
      };
      
      // Fetch assignments by halaqa rather than just by teacher
      let assignments = [];
      const halaqa = selectedHalaqa || currentHalaqa;
      
      if (halaqa) {
        try {
          // Get assignments for the current halaqa
          console.log("About to request assignments with halaqaId:", halaqa._id);
          const response = await api.get('/assignments/teacher', {
            params: {
              halaqaId: halaqa._id.toString(),
              center: user.center
            }
          });
          assignments = response.data || [];
          console.log('Fetched assignments for halaqa:', assignments.length);
        } catch (err) {
          console.error('Error fetching halaqa assignments:', err);
          assignments = await getTeacherAssignments(user.center);
        }
      } else {
        assignments = await getTeacherAssignments(user.center);
      }

      console.log('Fetched teacher assignments:', assignments.length);
      
      if (assignments && assignments.length > 0) {
        // Filter for assignments that need assessment (no assessment yet)
        const pendingItems = assignments
          .filter(item => !item.assessment || !item.assessment.rating)
          .map(item => ({
            id: item._id,
            studentId: item.students[0]?._id || 'default-student-id',
            studentName: item.students[0]?.name || 'Student',
            assignment: `${item.surah.name} (${item.verses.from}-${item.verses.to})`,
            dueDate: item.dueDate, // Store the actual dueDate
            date: formatDate(item.dueDate || item.updatedAt || item.createdAt),
            updatedAt: item.updatedAt,
            createdAt: item.createdAt
          }))
          // Sort by dueDate (most recent first)
          .sort((a, b) => {
            const dateA = new Date(a.dueDate || a.updatedAt || a.createdAt).getTime();
            const dateB = new Date(b.dueDate || b.updatedAt || b.createdAt).getTime();
            return dateB - dateA; // Descending order (most recent first)
          });
        
        dashboardData.pendingReviews = pendingItems.length;
        dashboardData.pendingReviewItems = pendingItems;
      }
      
      // Get the current halaqa student count
      if (halaqa) {
        try {
          const students = await halaqaService.getHalaqaStudents(halaqa._id);
          console.log('Student count from API:', students.length);
          
          // Set both the dashboard data and dedicated student count state
          dashboardData.studentCount = students.length;
          setStudentCount(students.length);
          
          console.log('dashboardData.studentCount is now:', dashboardData.studentCount);
        } catch (err) {
          console.error('Error fetching students count:', err);
        }
      }
      
      console.log('Final dashboard data before setting state:', dashboardData);
      setData(dashboardData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Initialize with empty data rather than mock data
      setData(initialDashboardData);
      setLoading(false);
      // Show error to user
      alert('Unable to load dashboard data. Please try again later.');
    }
  };

  // Fetch assignments
  const fetchAssignments = async (selectedHalaqa = null) => {
    try {
      // Fetch real assignments from the API
      const halaqa = selectedHalaqa || currentHalaqa;
      const params = halaqa ? { halaqaId: halaqa._id } : {};
      const assignmentsData = await getTeacherAssignments(user.center, halaqa?._id);
      console.log('Fetched real assignments:', assignmentsData);
      
      // Transform the data to match the expected format for your UI
      const formattedAssignments = assignmentsData.map(assignment => ({
        id: assignment._id,
        title: `${assignment.surah.name} (${assignment.verses.from}-${assignment.verses.to})`,
        type: assignment.type,
        students: assignment.students.map(student => 
          typeof student === 'object' ? student.name : student
        ),
        dueDate: formatDate(assignment.dueDate),
        status: assignment.status,
        assessment: assignment.assessment
      }));
      
      setAssignments(formattedAssignments);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      // Set empty array instead of mock data
      setAssignments([]);
      
      // Show error to user
      alert('Unable to load assignment data. Please try again later.');
    }
  };

  // Fetch center data
  const fetchCenterData = async () => {
    try {
      if (user && user.center) {
        const response = await api.get(`/centers/${user.center}`);
        setCenterData(response.data);
      }
    } catch (error) {
      console.error('Error fetching center data:', error);
    }
  };

  // Effect for initial data loading
  useEffect(() => {
    setLoading(true);
    
    const loadData = async () => {
      try {
        // First, load halaqa data and wait for it to complete
        await fetchHalaqaData();
        
        // Then fetch dashboard data and assignments
        await fetchDashboardData();
        await fetchAssignments();
        
        // Fetch center data
        await fetchCenterData();
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="loading-spinner-container">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="teacher-dashboard">
      {/* Dashboard Header */}
      <div style={{display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '15px'}}>
        <div style={{backgroundColor: '#4a90e2', borderRadius: '8px', padding: '6px 10px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
          <h1 style={{fontSize: '16px', margin: 0, color: 'Black', fontWeight: '500'}}>Teacher Dashboard</h1>
        </div>
        
      </div>

      {/* Welcome Message */}
      <div className="welcome-message">
        <h2>Welcome, {user?.name || 'Teacher'}</h2>
      </div>

      {/* Class Switcher */}
      {halaqas && halaqas.length > 0 && (
        <div className="class-switcher-container">
          <h3>Current Class</h3>
          <div className="class-switcher">
            <div 
              className="selected-class"
              onClick={toggleClassDropdown}
            >
              <span>{currentHalaqa ? currentHalaqa.name : halaqas[0].name}</span>
              <i className="dropdown-icon">▼</i>
            </div>
            
            {showClassDropdown && (
              <div className="class-dropdown">
                {halaqas.map((halaqa) => (
                  <div 
                    key={halaqa._id}
                    className={`class-item ${currentHalaqa && currentHalaqa._id === halaqa._id ? 'active' : ''}`}
                    onClick={() => selectClass(halaqa)}
                  >
                    <span>{halaqa.name}</span>
                    <span className="student-count">
                      {halaqaStudentCounts[halaqa._id] || 0} students
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            <button 
              className="view-students-btn"
              onClick={() => navigateToHalaqaStudents(currentHalaqa || halaqas[0])}
            >
              <span className="students-icon">👥</span> Students ({halaqaStudentCounts[currentHalaqa ? currentHalaqa._id : halaqas[0]._id] || 0})
            </button>
          </div>
        </div>
      )}
      
      {/* Stats Section */}
      <div style={{ 
        width: '100%',  
        marginBottom: '30px',
      }}>
        <div className="stats-card">
          <h3>Pending Assessments Today</h3>
          <div className="stat-value">{data.pendingReviewItems?.filter(item => isToday(item.dueDate || item.updatedAt || item.createdAt)).length || 0}</div>
          <div className="stat-label">Need Review</div>
        </div>
      </div>
      
      {/* Quick Actions Section */}
      <h2 className="section-title">Quick Actions</h2>
      <div className="quick-actions">
        <div className="action-card" onClick={handleNewAssignment}>
          <div className="action-icon book-icon"></div>
          <span className="action-label">New Assignment</span>
        </div>
        
        <div className="action-card" onClick={handleTakeAttendance}>
          <div className="action-icon calendar-icon"></div>
          <span className="action-label">Take Attendance</span>
        </div>
        
        <div className="action-card" onClick={handleViewProgress}>
          <div className="action-icon chart-icon"></div>
          <span className="action-label">View Progress</span>
        </div>
      </div>
      
      {/* Dashboard Content */}
      {/* User Profile Menu removed */}
      
      <div className="dashboard-content full-width">
        {/* Pending Assessments Panel */}
        <div className="dashboard-panel">
          <div className="panel-header">
            <h2>Pending Assessments Today</h2>
            <span className="sort-indicator">Due today</span>
          </div>
          
          {data.pendingReviewItems && data.pendingReviewItems.filter(item => isToday(item.dueDate || item.updatedAt || item.createdAt)).length > 0 ? (
            <div className="assessments-list">
              {data.pendingReviewItems
                .filter(item => isToday(item.dueDate || item.updatedAt || item.createdAt))
                .slice(0, displayLimit)
                .map((item) => (
                <div 
                  key={item.id} 
                  className="assessment-item"
                >
                  <div 
                    className="priority-indicator" 
                    style={{ backgroundColor: getPriorityColor(item) }}
                  ></div>
                  
                  <div className="assessment-details">
                    <h3>{item.studentName}</h3>
                    <div className="assignment-meta">{item.assignment}</div>
                  </div>
                  
                  <div className="assessment-meta">
                    <div className="due-date">Due: {item.date}</div>
                    {isPast(item.dueDate || new Date()) && (
                      <div className="overdue-indicator">Overdue</div>
                    )}
                  </div>
                  
                  <div className="assessment-actions">
                    <button 
                      className="assess-btn"
                      onClick={() => handleReviewItem(item)}
                    >
                      Assess
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">✓</div>
              <p>No pending assessments</p>
            </div>
          )}
          
          {/* Show more button if needed */}
          {data.pendingReviewItems && data.pendingReviewItems.filter(item => isToday(item.dueDate || item.updatedAt || item.createdAt)).length > displayLimit && (
            <div className="show-more">
              <button onClick={() => setDisplayLimit(prev => prev + 5)}>
                +{data.pendingReviewItems.filter(item => isToday(item.dueDate || item.updatedAt || item.createdAt)).length - displayLimit} more
              </button>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default TeacherDashboard;